import React, { useState } from 'react';
import { Phone, Mail, MapPin, Award, Users, Clock, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../utils/mock';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-28">
            <div className="flex items-center space-x-2">
              <img 
                src="/perez-logo.jpg" 
                alt="Perez & Fils Logo" 
                className="h-24 w-auto object-contain"
              />
              <div className="hidden md:block text-xs text-gray-600 ml-4 border-l border-gray-300 pl-4">Depuis 1964</div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('accueil')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Accueil
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('realisations')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Réalisations
              </button>
              <button onClick={() => scrollToSection('apropos')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                À propos
              </button>
              <button onClick={() => scrollToSection('temoignages')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Témoignages
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Contact
              </button>
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <a href={`tel:${mockData.contact.phone}`} className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors">
                <Phone size={18} />
                <span className="font-semibold">{mockData.contact.phone}</span>
              </a>
              <Button onClick={() => scrollToSection('contact')} className="bg-blue-600 hover:bg-blue-700 text-white">
                Devis gratuit
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-blue-600"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <nav className="flex flex-col py-4">
              <button onClick={() => scrollToSection('accueil')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                Accueil
              </button>
              <button onClick={() => scrollToSection('services')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                Services
              </button>
              <button onClick={() => scrollToSection('realisations')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                Réalisations
              </button>
              <button onClick={() => scrollToSection('apropos')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                À propos
              </button>
              <button onClick={() => scrollToSection('temoignages')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                Témoignages
              </button>
              <button onClick={() => scrollToSection('contact')} className="px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                Contact
              </button>
              <div className="px-4 py-3">
                <Button onClick={() => scrollToSection('contact')} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Devis gratuit
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="accueil" className="relative pt-20 min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={mockData.images.hero}
            alt="Perez & Fils - Expertise en isolation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/70 to-blue-900/50"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl py-20 md:py-32">
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
              <span className="text-white text-sm font-medium">Expertise et tradition familiale depuis 1964</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Votre expert en isolation et plâtrerie
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              QUALIBAT et RGE, nous transformons vos projets de rénovation et construction avec excellence et savoir-faire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="bg-white text-blue-900 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              >
                Demander un devis
              </Button>
              <Button
                size="lg"
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold text-lg px-8 py-6"
              >
                Nos services
              </Button>
            </div>
          </div>
        </div>

        <button
          onClick={() => scrollToSection('services')}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce cursor-pointer"
        >
          <ChevronDown size={32} />
        </button>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-900 mb-2">{stat.value}</div>
                <div className="text-gray-700 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Nos Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une expertise complète en isolation et plâtrerie pour tous vos projets de construction et rénovation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.services.map((service, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="mb-4 h-48 overflow-hidden rounded-lg">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                    <div className="text-blue-600 group-hover:text-white transition-colors duration-300">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Réalisations Section */}
      <section id="realisations" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Nos Réalisations</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez quelques-uns de nos projets réalisés avec soin et expertise
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.realisations.map((real, index) => (
              <Card key={index} className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={real.image}
                    alt={real.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl font-bold text-white mb-2">{real.title}</h3>
                    <p className="text-white/90 text-sm">{real.description}</p>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-blue-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {real.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{real.category}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* À propos Section */}
      <section id="apropos" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
                60 ans d'excellence et de savoir-faire
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Créée en 1964 par M. Jean PEREZ, l'entreprise familiale est devenue SARL PEREZ & Fils en 1993, puis SAS en 2017.
                </p>
                <p>
                  Aujourd'hui, cette PME moderne et dynamique continue de bénéficier d'une solide réputation grâce à son sérieux, sa rigueur et son savoir-faire.
                </p>
                <p>
                  Spécialisée dans le second œuvre, l'entreprise intervient sur la réalisation de faux plafonds et cloisons, doublages, plafonds, isolation thermique et acoustique, ainsi que sur des faux plafonds décoratifs.
                </p>
                <p>
                  Elle accompagne la construction, la réhabilitation et l'aménagement de villas, appartements privés ou publics, bureaux, locaux commerciaux et tertiaires, ainsi que les projets des collectivités et organismes publics.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Award className="text-blue-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-bold text-blue-900">Certification QUALIBAT</div>
                    <div className="text-sm text-gray-600">Gage de qualité et d'expertise</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="text-blue-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-bold text-blue-900">Agrément RGE</div>
                    <div className="text-sm text-gray-600">Travaux éligibles aux CEE</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={mockData.images.about}
                  alt="À propos de Perez & Fils"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-8 rounded-lg shadow-xl max-w-xs">
                <div className="text-4xl font-bold mb-2">60 ans</div>
                <div className="text-lg">d'expertise et de tradition familiale</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages Section */}
      <section id="temoignages" className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Témoignages Clients</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La satisfaction de nos clients est notre plus belle récompense
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <Users className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <div className="font-bold text-blue-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.project}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Questions Fréquentes</h2>
              <p className="text-lg text-gray-600">Trouvez les réponses à vos questions</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-blue-900 hover:text-blue-600">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Contactez-nous</h2>
            <p className="text-lg text-gray-600">Nous sommes à votre écoute pour tous vos projets</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <Card className="h-full border-2 border-blue-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-6">Demande de devis</h3>
                  <form className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="Votre nom"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="Votre prénom"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type de projet</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                        <option>Sélectionnez un service</option>
                        <option>Isolation des combles</option>
                        <option>Isolation des murs</option>
                        <option>Plâtrerie</option>
                        <option>Faux plafonds</option>
                        <option>Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <textarea
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        placeholder="Décrivez votre projet..."
                      ></textarea>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg">
                      Envoyer ma demande
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-2 border-blue-200">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-6">Nos coordonnées</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900 mb-1">Adresse</div>
                        <div className="text-gray-700">
                          {mockData.contact.address.line1}<br />
                          {mockData.contact.address.line2}<br />
                          {mockData.contact.address.city}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900 mb-1">Téléphone</div>
                        <a href={`tel:${mockData.contact.phone}`} className="text-gray-700 hover:text-blue-600 transition-colors">
                          {mockData.contact.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900 mb-1">Email</div>
                        <a href={`mailto:${mockData.contact.email}`} className="text-gray-700 hover:text-blue-600 transition-colors">
                          {mockData.contact.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900 mb-1">Horaires</div>
                        <div className="text-gray-700">
                          Lundi - Vendredi : 8h00 - 18h00<br />
                          Samedi : Sur rendez-vous
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-600 border-0 text-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-4">Intervention rapide</h3>
                  <p className="mb-6">
                    Nous intervenons dans toute la région et répondons à vos demandes dans les meilleurs délais.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Lot-et-Garonne</span>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Gironde</span>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Landes</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/perez-logo.jpg" 
                alt="Perez & Fils Logo" 
                className="h-16 w-auto object-contain mb-4 brightness-0 invert"
              />
              <p className="text-blue-200 text-sm">
                Expertise et tradition familiale depuis 1964
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-blue-200">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Isolation des combles</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Isolation des murs</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Plâtrerie</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Faux plafonds</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Liens rapides</h4>
              <ul className="space-y-2 text-sm text-blue-200">
                <li><button onClick={() => scrollToSection('apropos')} className="hover:text-white transition-colors">À propos</button></li>
                <li><button onClick={() => scrollToSection('realisations')} className="hover:text-white transition-colors">Réalisations</button></li>
                <li><button onClick={() => scrollToSection('temoignages')} className="hover:text-white transition-colors">Témoignages</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Certifications</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Award size={20} className="text-blue-300" />
                  <span className="text-sm text-blue-200">QUALIBAT</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award size={20} className="text-blue-300" />
                  <span className="text-sm text-blue-200">RGE</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-blue-800 pt-8 text-center text-sm text-blue-200">
            <p>&copy; {new Date().getFullYear()} SAS PEREZ & FILS. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
